/**
 * useTermsAcceptance hook
 *
 * Checks whether the current user has accepted the latest Terms of Service
 * and Privacy Policy. Exposes a modal trigger and accept/decline handlers.
 *
 * Anonymous-user behaviour: per redesign decision v1.2, buyers can pay
 * without signing in. For anonymous users, `checkAcceptance` shows the
 * same modal but `handleAccept` records acceptance in client state only
 * (no /user/accept-terms call, since there's no account to write
 * against). The acceptance moment is also captured by Stripe Checkout's
 * terms page, so we have a second affirmation in the legal trail. When
 * the user later signs in (e.g. post-payment "Save to your account"),
 * we'll re-prompt only if the stored versions don't match.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ApiClientError } from '../utils/api';

// Default to empty so calls go same-origin (CloudFront proxies /api/* to API
// Gateway).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const CURRENT_TOS_VERSION = '2026-03-01';
const CURRENT_PRIVACY_VERSION = '2026-03-01';

interface UseTermsAcceptanceReturn {
  /** Whether the terms modal should be shown */
  showModal: boolean;
  /** Check if terms are accepted; shows modal if not. Returns true if accepted. */
  checkAcceptance: () => Promise<boolean>;
  /** Accept terms and close modal */
  handleAccept: () => Promise<void>;
  /** Decline terms and close modal */
  handleDecline: () => void;
  /** Whether the accept request is in progress */
  isAccepting: boolean;
  /** Error message if acceptance failed */
  error: string | null;
}

export function useTermsAcceptance(): UseTermsAcceptanceReturn {
  const { isAuthenticated } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const checkAcceptance = useCallback(async (): Promise<boolean> => {
    if (accepted) return true;

    // Anonymous users have no account to check against — go straight to
    // the modal. They affirm in-app + again on Stripe's checkout page.
    if (!isAuthenticated) {
      setShowModal(true);
      return false;
    }

    // Authenticated users: check the server-recorded acceptance state.
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/terms-status`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        if (
          data.tosVersion === CURRENT_TOS_VERSION &&
          data.privacyPolicyVersion === CURRENT_PRIVACY_VERSION
        ) {
          setAccepted(true);
          return true;
        }
      }
    } catch {
      // If the endpoint doesn't exist yet, fall through to show modal
    }

    setShowModal(true);
    return false;
  }, [isAuthenticated, accepted]);

  const handleAccept = useCallback(async () => {
    setIsAccepting(true);
    setError(null);

    // Anonymous: record acceptance in client state only. Stripe Checkout
    // will capture the legal affirmation again on its hosted page.
    if (!isAuthenticated) {
      setAccepted(true);
      setShowModal(false);
      setIsAccepting(false);
      return;
    }

    // Authenticated: persist acceptance to the user record.
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/accept-terms`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tosVersion: CURRENT_TOS_VERSION,
          privacyPolicyVersion: CURRENT_PRIVACY_VERSION,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new ApiClientError(
          data.error || 'Could not record acceptance',
          data.code || 'ACCEPT_ERROR'
        );
      }

      setAccepted(true);
      setShowModal(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not record acceptance'
      );
    } finally {
      setIsAccepting(false);
    }
  }, [isAuthenticated]);

  const handleDecline = useCallback(() => {
    setShowModal(false);
  }, []);

  return {
    showModal,
    checkAcceptance,
    handleAccept,
    handleDecline,
    isAccepting,
    error,
  };
}
